import { useState } from "react";
import { heroWords, hotWords, hotRanking } from "@/mocks/siteData";

interface Props {
  query: string;
  onQueryChange: (v: string) => void;
}

export default function Hero({ query, onQueryChange }: Props) {
  const [kw, setKw] = useState(query);
  const q = kw.trim().toLowerCase();

  const live = hotRanking
    .filter((it) => !q || it.title.toLowerCase().includes(q) || it.tag.includes(q))
    .slice(0, 5);

  const apply = (v: string) => {
    setKw(v);
    onQueryChange(v);
  };

  const goHot = () => {
    document.getElementById("hot")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" className="relative overflow-hidden bg-background-50">
      {/* decorative shapes */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-primary-100/70"></div>
      <div className="pointer-events-none absolute top-40 right-24 w-40 h-40 rounded-full bg-accent-200/50"></div>
      <div className="pointer-events-none absolute -left-20 bottom-0 w-64 h-64 rounded-full bg-accent-100/60"></div>

      <div className="relative mx-auto max-w-7xl px-4 md:px-6 pt-28 pb-16 md:pt-36 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left content */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs md:text-sm font-medium mb-5">
              <i className="ri-fire-fill text-accent-600 text-base w-4 h-4 flex items-center justify-center"></i>
              娱乐热讯轻门户 · 全天滚动更新
            </div>

            <h1 className="font-heading font-black text-foreground-950 leading-tight text-4xl sm:text-5xl md:text-6xl tracking-tight">
              九色爆料
              <span className="block mt-2 text-primary-600">热点不止，吃瓜不掉线</span>
            </h1>

            <p className="mt-5 max-w-xl text-foreground-600 text-base md:text-lg leading-relaxed">
              汇聚影视综艺、明星动态与现场演出的最新话题，今日热榜与新瓜速递实时同步。热门话题榜一网打尽，追热点不再迷路。
            </p>

            {/* keyword chips */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 mr-1">今日热词</span>
              {hotWords.map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={goHot}
                  className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-foreground-700 text-xs md:text-sm hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  #{w}
                </button>
              ))}
            </div>

            {/* search */}
            <form
              className="mt-7 flex flex-col sm:flex-row w-full max-w-xl gap-0"
              onSubmit={(e) => {
                e.preventDefault();
                goHot();
              }}
              role="search"
            >
              <div className="flex flex-1 items-center gap-2 px-4 rounded-t-md sm:rounded-l-md sm:rounded-tr-none bg-background-100 border border-background-200 focus-within:border-primary-300">
                <i className="ri-search-line text-foreground-400 text-base w-4 h-4 flex items-center justify-center"></i>
                <input
                  value={kw}
                  onChange={(e) => apply(e.target.value)}
                  placeholder="搜索感兴趣的话题或栏目…"
                  className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 py-3.5 outline-none"
                />
              </div>
              <button
                type="submit"
                className="px-6 py-3.5 rounded-b-md sm:rounded-r-md sm:rounded-bl-none bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                一键搜热点
              </button>
            </form>

            {/* stats */}
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
              {heroWords.map((s) => (
                <div key={s.label} className="flex items-baseline gap-2">
                  <span className="font-heading font-black text-2xl md:text-3xl text-primary-600">{s.count}</span>
                  <span className="text-sm text-foreground-600">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right live panel */}
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl border border-background-200 bg-background-100/80 overflow-hidden animate-fadeUp">
              <div className="flex items-center justify-between px-5 py-4 bg-primary-500 text-background-50">
                <div className="flex items-center gap-2">
                  <span className="relative flex w-2.5 h-2.5">
                    <span className="absolute inline-flex w-full h-full rounded-full bg-accent-300 animate-pulseDot"></span>
                    <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-background-50"></span>
                  </span>
                  <h2 className="font-heading font-bold text-base">实时话题 · 热度速览</h2>
                </div>
                <span className="text-xs bg-background-50/20 px-2 py-1 rounded-md">{live.length} 条</span>
              </div>

              <ul className="divide-y divide-background-200">
                {live.map((it, i) => (
                  <li key={it.rank} className="flex items-center gap-3 px-5 py-3.5">
                    <span
                      className={`flex items-center justify-center w-6 h-6 rounded-md font-heading font-black text-xs shrink-0 ${
                        i === 0
                          ? "bg-accent-500 text-primary-950"
                          : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <a href="#hot" className="flex-1 min-w-0">
                      <p className="truncate text-sm text-foreground-900 hover:text-primary-600 transition-colors cursor-pointer">
                        {it.title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[11px] text-primary-600 font-medium">{it.tag}</span>
                        <span className="text-[11px] text-foreground-400">{it.heatLabel} 热度</span>
                      </div>
                    </a>
                    <i
                      className={`text-base w-4 h-4 flex items-center justify-center ${
                        it.trend === "up"
                          ? "text-primary-500 ri-arrow-up-line"
                          : it.trend === "down"
                          ? "text-foreground-300 ri-arrow-down-line"
                          : "text-foreground-300 ri-arrow-right-line"
                      }`}
                    ></i>
                  </li>
                ))}
                {live.length === 0 && (
                  <li className="px-5 py-10 text-center text-sm text-foreground-500">没有匹配“{kw}”的话题</li>
                )}
              </ul>

              <button
                type="button"
                onClick={goHot}
                className="w-full px-5 py-3.5 text-center text-sm font-semibold text-primary-700 hover:bg-primary-50 transition-colors cursor-pointer"
              >
                查看完整热榜 <i className="ri-arrow-right-line text-sm w-4 h-4 inline-flex items-center justify-center"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}